import {
  EntityEvent,
  EntityType,
  EntryType,
  Octopus,
  RelatedEntry,
  ModuleType,
  Field,
} from '@maximizer/core/shared/domain';
import { MenuOptionName, UserMenuOptionName } from '../enums';

export enum ActionEventType {
  ActionClose = 'actionClose',
  BannerAction = 'bannerAction',
  BannerClose = 'bannerClose',
  Cancel = 'cancel',
  CopyToClipboard = 'copyToClipboard',
  ChangeCurrentSelection = 'changeCurrentSelection',
  ContentResize = 'contentResize',
  EditList = 'editList',
  EditEntity = 'editEntity',
  EditDetails = 'editDetails',
  EditKeyFields = 'editKeyFields',
  FinishPhotoUpload = 'finishPhotoUpload',
  GlobalEdit = 'openGlobalEdit',
  Initialize = 'initializeComponent',
  Load = 'load',
  OpenCoachingPlaybook = 'openCoachingPlaybook',
  OpenEntity = 'openActivities',
  OpenEntry = 'openDetails',
  OpenPicker = 'openPicker',
  OpenPrintView = 'openPrintView',
  OpenMonitoring = 'openMonitoring',
  OpenPhotoUpload = 'openPhotoUpload',
  DeleteAppointment = 'deleteAppointment',
  Refresh = 'refresh',
  RefreshGrid = 'refreshGrid',
  RefreshOpportunity = 'refreshOpportunity',
  RefreshKeyFieldLists = 'refreshKeyFieldLists',
  RemoveList = 'removeList',
  Save = 'save',
  SavePhotoUpload = 'savePhotoUpload',
  SetFieldValue = 'setFieldValue',
  SetAccessValue = 'setAccessValue',
  SetLayout = 'setLayout',
  SetNavigationPath = 'setNavigationPath',
  Show = 'show',
  ShowDialog = 'showDialog',
  ShowCustomDialog = 'showCustomDialog',
  SwitchModule = 'switchModule',
  ChangeCurrentModule = 'changeCurrentModule',
  ToggleIconBarCollapse = 'toggleIconBarCollapse',
  ValueChanged = 'valueChanged',
  ViewDetails = 'viewDetails',
  TabSelection = 'tabSelection',
  TriggerMenuAction = 'triggerMenuAction',
  TriggerUserMenuAction = 'triggerUserMenuAction',
  RequestSelectedGridEntries = 'requestSelectedGridEntries',
  ReturnDialogResult = 'returnDialogResult',
  ReturnCustomDialogResult = 'returnCustomDialogResult',
  UpdateParent = 'updateParent',
  TaskOutcomeSelect = 'taskOutcomeSelect',
}

export class ActionArgs {
  constructor(public event: string) {}
}

export class KeyArgs extends ActionArgs {
  constructor(
    event: string,
    public key?: string,
  ) {
    super(event);
  }
}

export class CancelArgs extends ActionArgs {
  constructor() {
    super(ActionEventType.Cancel);
  }
}

export class OpenFieldPickerArgs extends ActionArgs {
  constructor(
    public field: Field,
    public form: unknown,
  ) {
    super(ActionEventType.OpenPicker);
  }
}

export class EditListArgs extends ActionArgs {
  constructor(
    public field: string,
    public currentValue: unknown,
  ) {
    super(ActionEventType.EditList);
  }
}

export class EntrySavedArgs extends ActionArgs {
  constructor(
    public entryType: EntryType,
    public key: string,
    public isNew: boolean,
  ) {
    super(ActionEventType.Save);
  }
}

export class EntryValueChangedArgs extends ActionArgs {
  constructor(
    public entryType: EntryType,
    public value: unknown,
  ) {
    super(ActionEventType.ValueChanged);
  }
}

export class OpenEntryArgs extends ActionArgs {
  constructor(public entity: RelatedEntry) {
    super(ActionEventType.OpenEntry);
  }
}

export class OpenEntityArgs extends ActionArgs {
  constructor(public entity: EntityEvent) {
    super(ActionEventType.OpenEntity);
  }
}

export class TriggerUserMenuActionArgs extends ActionArgs {
  constructor(public actionType: UserMenuOptionName) {
    super(ActionEventType.TriggerUserMenuAction);
  }
}

export class DeleteAppointmentArgs extends ActionArgs {
  constructor(
    public key: string,
    public type: Octopus.InteractionCodeString,
  ) {
    super(ActionEventType.DeleteAppointment);
  }
}

export class GlobalEditArgs extends ActionArgs {
  constructor(
    public type: EntityType,
    public keys: string[],
  ) {
    super(ActionEventType.GlobalEdit);
  }
}

export class EditEntityArgs extends ActionArgs {
  constructor(
    public entity: EntityEvent,
    public moduleId?: ModuleType,
  ) {
    super(ActionEventType.EditEntity);
  }
}

export class TriggerMenuActionArgs extends ActionArgs {
  constructor(
    public actionType: MenuOptionName,
    public key: string,
    public moduleId: ModuleType,
  ) {
    super(ActionEventType.TriggerMenuAction);
  }
}

export class CopyToClipboardArgs extends ActionArgs {
  constructor(public value: string) {
    super(ActionEventType.CopyToClipboard);
  }
}

export class OpenPrintViewArgs extends ActionArgs {
  constructor(
    public type: string,
    public key: string,
  ) {
    super(ActionEventType.OpenPrintView);
  }
}

export class OpenCoachingPlaybookArgs extends ActionArgs {
  constructor(public key: string) {
    super(ActionEventType.OpenCoachingPlaybook);
  }
}

export class OpenMonitoringArgs extends ActionArgs {
  constructor(public key: string) {
    super(ActionEventType.OpenMonitoring);
  }
}

export class EditDetailsArgs extends ActionArgs {
  constructor() {
    super(ActionEventType.EditDetails);
  }
}

export class ViewDetailsArgs extends ActionArgs {
  constructor() {
    super(ActionEventType.ViewDetails);
  }
}

export class UpdateParentArgs extends ActionArgs {
  constructor(public key: string) {
    super(ActionEventType.UpdateParent);
  }
}

export class ChangeCurrentSelection extends ActionArgs {
  constructor(
    public key: string,
    public target?: string,
  ) {
    super(ActionEventType.ChangeCurrentSelection);
  }
}

export class SwitchModule extends ActionArgs {
  constructor(
    public target: string,
    public args?: {
      moduleType: string;
      isMarketing?: boolean;
      externalUrl?: string;
    },
  ) {
    super(ActionEventType.SwitchModule);
  }
}

export class ChangeCurrentModule extends ActionArgs {
  constructor(
    public target: string,
    public args?: { url?: string },
  ) {
    super(ActionEventType.ChangeCurrentModule);
  }
}

export class TabSelection extends ActionArgs {
  [key: string]: unknown;
  constructor(
    public targetId: string,
    public args?: { [key: string]: unknown },
  ) {
    super(ActionEventType.TabSelection);
  }
}
export class ToggleIconBarCollapseArgs extends ActionArgs {
  constructor(public collapse: boolean) {
    super(ActionEventType.ToggleIconBarCollapse);
  }
}

export class TaskOutcomeSelectArgs extends ActionArgs {
  constructor(
    public taskKey: string,
    public cancelled: boolean,
    public outcome?: string,
    public outcomeWorkflowKey?: string,
  ) {
    super(ActionEventType.TaskOutcomeSelect);
  }
}

export class ContentResizeArgs extends ActionArgs {
  constructor() {
    super(ActionEventType.ContentResize);
  }
}

export class BannerDoActionArgs extends ActionArgs {
  constructor(public target: string) {
    super(ActionEventType.BannerAction);
  }
}

export class BannerCloseArgs extends ActionArgs {
  constructor(public target: string) {
    super(ActionEventType.BannerClose);
  }
}
